import { getTokenSilently } from '@contexts/AuthContext/auth';
import { DOMAIN } from '@shared/frontendEnv';
import { useCallback, useEffect, useRef, useState } from 'react';
import { logger } from '../../../utils/logger';

export type RoomEventType =
  | 'join-waiting-room'
  | 'leave-waiting-room'
  | 'join-meeting'
  | 'leave-meeting';

const useMeetingWebSocket = () => {
  const webSocketRef = useRef<WebSocket>();
  const roomEventQueueRef = useRef<Promise<void>>(Promise.resolve());
  const manuallyClosed = useRef(false);
  const reconnectTimeoutRef = useRef<NodeJS.Timer>();
  const elapsedTimeRef = useRef(0);
  const [isSocketConnected, setIsSocketConnected] = useState(false);
  const pingIntervalRef = useRef<NodeJS.Timer>();
  const pongTimeoutRef = useRef<NodeJS.Timer>();

  const maxReconnectTime = 300; // Maximum reconnect time in seconds (5 minutes)
  const reconnectInterval = 5000; // Reconnect every 5 seconds
  const pingEvery = 10000;
  const pongTimeout = 5000;

  const connectWebSocket = () => {
    if (
      webSocketRef.current &&
      (webSocketRef.current.readyState === WebSocket.OPEN ||
        webSocketRef.current.readyState === WebSocket.CONNECTING)
    ) {
      logger.info('WebSocket already connected or connecting');
      return;
    }

    const url = `wss://${DOMAIN}/api/room-activity-ws`;

    if (webSocketRef.current && webSocketRef.current.readyState !== WebSocket.CLOSED) {
      webSocketRef.current.close();
    }

    const ws = new WebSocket(url);
    manuallyClosed.current = false;

    ws.onopen = () => {
      logger.info('WebSocket connected');
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      setIsSocketConnected(true);

      elapsedTimeRef.current = 0;

      pingIntervalRef.current = setInterval(() => {
        if (ws.readyState !== WebSocket.OPEN) return;

        ws.send(JSON.stringify({ type: 'ping' }));

        pongTimeoutRef.current = setTimeout(() => {
          logger.warn('Pong not received, closing socket');
          ws.close();
        }, pongTimeout);
      }, pingEvery);
    };

    ws.onmessage = (e) => {
      const message = JSON.parse(e.data);

      if (message.type === 'pong') {
        clearTimeout(pongTimeoutRef.current);
        return;
      }
    };

    ws.onclose = () => {
      logger.info('WebSocket disconnected');

      if (!manuallyClosed.current) {
        setIsSocketConnected(false);
      }

      clearInterval(pingIntervalRef.current);
      clearTimeout(pongTimeoutRef.current);

      if (!manuallyClosed.current && elapsedTimeRef.current < maxReconnectTime) {
        reconnectTimeoutRef.current = setTimeout(() => {
          elapsedTimeRef.current += reconnectInterval / 1000;
          connectWebSocket();
          logger.info(
            `Reconnecting in ${reconnectInterval / 1000} seconds... Total elapsed time: ${
              elapsedTimeRef.current
            } seconds`
          );
        }, reconnectInterval);
      } else if (manuallyClosed.current) {
        logger.info('WebSocket closed intentionally, no reconnection.');
      } else {
        logger.error('Max reconnect time reached. Stopping reconnection attempts.');
      }
    };

    ws.onerror = (error) => {
      logger.error('WebSocket error encountered:', error);
      ws.close();
    };

    webSocketRef.current = ws;
  };

  const closeWebsocketConnection = () => {
    manuallyClosed.current = true;
    if (webSocketRef.current) {
      webSocketRef.current.close();
    }
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }
  };

  const sendRoomEvent = useCallback((type: RoomEventType, meetingId: string) => {
    roomEventQueueRef.current = roomEventQueueRef.current
      .then(async () => {
        if (webSocketRef.current?.readyState !== WebSocket.OPEN) return;

        const token: string = await getTokenSilently();
        const ws = webSocketRef.current;

        if (ws?.readyState !== WebSocket.OPEN) return;

        ws.send(JSON.stringify({ type, meetingId, token: `Bearer ${token}` }));
      })
      .catch((error) => logger.error(`Failed to send ${type}:`, error));

    return roomEventQueueRef.current;
  }, []);

  useEffect(() => {
    connectWebSocket();

    const offlineHandler = () => webSocketRef.current?.close();
    const onlineHandler = () => {
      if (!webSocketRef.current || webSocketRef.current.readyState === WebSocket.CLOSED) {
        connectWebSocket();
      }
    };
    window.addEventListener('online', onlineHandler);
    window.addEventListener('offline', offlineHandler);

    return () => {
      closeWebsocketConnection();

      window.removeEventListener('online', onlineHandler);
      window.removeEventListener('offline', offlineHandler);
    };
  }, []);

  return {
    webSocket: webSocketRef.current,
    isSocketConnected,
    closeWebsocketConnection,
    sendRoomEvent,
  };
};

export default useMeetingWebSocket;
